import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, CircularProgress, FormHelperText, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import type { InferType } from 'yup';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';
import { useStationsFilter } from '@/hooks/useStationsFilter';

import { handleError } from '@/utils/errorUtils';
import { capitalize } from '@/utils/stringUtils';

import type { TEntityOption } from '@/components/filter/types';
import { SimpleError } from '@/components/formik/error/SimpleError';
import type { TErrorProps } from '@/components/formik/error/types';
import { FormikForm } from '@/components/formik/FormikForm';
import { FormikText } from '@/components/formik/FormikText';
import { GenericModal } from '@/components/modal/GenericModal';
import { GenericModalClose } from '@/components/modal/GenericModalClose';
import { UploadCatalogStationSelect } from '@/components/uploadCatalog/UploadCatalogStationSelect';

import type { TOnSubmit } from '@/types/formTypes';
import type { TCatalogItem, TCreateCatalogItem } from '@/types/productCatalog';

import { useCreateCatalogItem } from '@/mutations/catalog';

import { createCatalogItemSchema } from '@/validation/catalog';

import { styles } from './CreateCatalogItem.styles';

export type TCreateCatalogItemProps = TModalsContext & {
  /** Preselects the popup's site (the first one). A site picked inside the popup stays in the popup. */
  stationIds?: string[];
  onSuccess: (item: TCatalogItem) => void;
};

type TCreateCatalogItemForm = InferType<typeof createCatalogItemSchema>;

const MODAL_NAME = 'productCatalog.create';

const CompactError = <PError extends unknown>(props: TErrorProps<PError>) => {
  const { error, errorsShown } = props;

  if (!error || errorsShown === false) {
    return null;
  }

  return <SimpleError {...(props as TErrorProps<string>)} withIcon={false} />;
};

const toPayload = (values: TCreateCatalogItemForm): TCreateCatalogItem => ({
  storeId: values.storeId,
  itemNumber: values.itemNumber.trim(),
  productDescription: values.productDescription.trim(),
  ...(values.vendorName?.trim() ? { vendorName: values.vendorName.trim() } : {}),
  ...(values.shelfLocation?.trim() ? { shelfLocation: values.shelfLocation.trim() } : {}),
  ...(values.category?.trim() ? { category: values.category.trim() } : {}),
  ...(values.retailPrice !== undefined && values.retailPrice !== null
    ? { retailPrice: values.retailPrice }
    : {}),
  ...(values.unitCost !== undefined && values.unitCost !== null
    ? { unitCost: values.unitCost }
    : {}),
});

const StationField = ({
  station,
  onStationChange,
  lookupStations,
}: {
  station: TEntityOption | null;
  onStationChange: (station: TEntityOption | null) => void;
  lookupStations: (query: string, signal: AbortSignal) => Promise<TEntityOption[]>;
}) => {
  const { t } = useTranslation();
  const { setFieldValue, submitCount, errors, isSubmitting } =
    useFormikContext<TCreateCatalogItemForm>();
  const showError = submitCount > 0 && Boolean(errors.storeId);
  const stationLabel = t('productCatalog.import.selectStationLabel');

  return (
    <Box sx={styles.fieldFull}>
      <UploadCatalogStationSelect
        value={station}
        getOptions={lookupStations}
        disabled={isSubmitting}
        onChange={(next) => {
          onStationChange(next);
          setFieldValue('storeId', next?.id ?? '', true);
        }}
      />
      {showError && (
        <FormHelperText error>
          {t(String(errors.storeId), { label: capitalize(stationLabel) })}
        </FormHelperText>
      )}
    </Box>
  );
};

const ModalClose = ({ onClose }: { onClose: () => void }) => {
  const { isSubmitting } = useFormikContext<TCreateCatalogItemForm>();

  return <GenericModalClose onClick={onClose} disabled={isSubmitting} />;
};

const CancelButton = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { isSubmitting } = useFormikContext<TCreateCatalogItemForm>();

  return (
    <Button variant="outlined" onClick={onClose} disabled={isSubmitting} sx={styles.cancelButton}>
      {t('common.cancel')}
    </Button>
  );
};

const SubmitButton = () => {
  const { t } = useTranslation();
  const { isSubmitting, isValid, submitCount, setFormikState } =
    useFormikContext<TCreateCatalogItemForm>();

  const highlightErrors = () => {
    setFormikState((state) => ({ ...state, submitCount: submitCount + 1 }));
  };

  return (
    <Box onClick={!isValid ? highlightErrors : undefined} sx={{ cursor: 'pointer' }}>
      <Button
        type="submit"
        variant="contained"
        disabled={isSubmitting || !isValid}
        sx={styles.submitButton}
      >
        {t('productCatalog.create.submit')}
        {isSubmitting && <CircularProgress size={18} sx={{ ml: 1, color: 'inherit' }} />}
      </Button>
    </Box>
  );
};

const Field = ({
  name,
  label,
  type,
  fullWidth,
}: {
  name: string;
  label: string;
  type?: 'text' | 'number';
  fullWidth?: boolean;
}) => (
  <Box sx={fullWidth ? [styles.field, styles.fieldFull] : styles.field}>
    <FormikText
      name={name}
      label={label}
      type={type}
      fullWidth
      HelperText={CompactError}
      {...(type === 'number' ? { inputProps: { min: 0, step: '0.01' } } : {})}
    />
  </Box>
);

export const CreateCatalogItem = ({
  close,
  lock,
  stationIds: initialStationIds,
  onSuccess,
}: TCreateCatalogItemProps) => {
  const { t } = useTranslation();
  const { lookupStations, restoreStations } = useStationsFilter('portalScope');
  // W4: saves the item and marks every saved catalog list and filter option old.
  const { mutateAsync: createItem } = useCreateCatalogItem();

  const [station, setStation] = useState<TEntityOption | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const initialStationId = initialStationIds?.find(Boolean);

  // The latest `restoreStations`, read inside the effect below. Its identity changes whenever the
  // portal's site names refresh (a station was renamed or created); the effect must not restore
  // again for that, because it would replace the site the operator picked in this popup.
  const restoreStationsRef = useRef(restoreStations);
  restoreStationsRef.current = restoreStations;

  /**
   * Restores the preselected site's name, once per preselected site id. Goes through the saved
   * station queries (`useStationsFilter`), so a site seen before shows at once.
   */
  useEffect(() => {
    if (!initialStationId) {
      return;
    }

    let relevant = true;
    Promise.resolve(restoreStationsRef.current([initialStationId])).then((options) => {
      if (!relevant || !options[0]) {
        return;
      }

      setStation(options[0]);
    });

    return () => {
      relevant = false;
    };
  }, [initialStationId]);

  const handleClose = () => {
    close(MODAL_NAME);
  };

  const handleStationChange = (next: TEntityOption | null) => {
    // Popup-only: the pick sets this item's site and changes nothing outside the popup.
    setStation(next);
    setSubmitError(null);
  };

  const onSubmit: TOnSubmit<TCreateCatalogItemForm> = async (values, { setSubmitting }) => {
    setSubmitError(null);
    lock(MODAL_NAME, () => false);

    try {
      const item = await createItem(toPayload(values));
      onSuccess(item);
      close(MODAL_NAME);
    } catch (error) {
      handleError(error, 'UNHANDLED_RESPONSE');
      setSubmitError(t('productCatalog.create.failedRequest'));
      setSubmitting(false);
      lock(MODAL_NAME, () => true);
    }
  };

  return (
    <GenericModal sx={styles.modal}>
      <FormikForm
        validationSchema={createCatalogItemSchema}
        initialValues={{
          storeId: initialStationId ?? '',
          itemNumber: '',
          vendorName: '',
          productDescription: '',
          shelfLocation: '',
          category: '',
        }}
        onSubmit={onSubmit}
        sx={styles.form}
      >
        <ModalClose onClose={handleClose} />

        <Box sx={styles.header}>
          <Typography sx={styles.title}>{t('productCatalog.create.title')}</Typography>
          <Typography sx={styles.description}>{t('productCatalog.create.description')}</Typography>
        </Box>

        <Box sx={styles.body}>
          <StationField
            station={station}
            onStationChange={handleStationChange}
            lookupStations={lookupStations}
          />

          <Box sx={styles.fieldRow}>
            <Field name="itemNumber" label="productCatalog.create.fields.itemNumber" />
            <Field name="vendorName" label="productCatalog.create.fields.vendorName" />
          </Box>

          <Field
            name="productDescription"
            label="productCatalog.create.fields.productDescription"
            fullWidth
          />

          <Box sx={styles.fieldRow}>
            <Field name="shelfLocation" label="productCatalog.create.fields.shelfLocation" />
            <Field name="category" label="productCatalog.create.fields.category" />
          </Box>

          <Box sx={styles.fieldRow}>
            <Field
              name="retailPrice"
              label="productCatalog.create.fields.retailPrice"
              type="number"
            />
            <Field name="unitCost" label="productCatalog.create.fields.unitCost" type="number" />
          </Box>

          {submitError && <FormHelperText error>{submitError}</FormHelperText>}
        </Box>

        <Box sx={styles.footer}>
          <CancelButton onClose={handleClose} />
          <SubmitButton />
        </Box>
      </FormikForm>
    </GenericModal>
  );
};
